import { SEMI_ADVANCE_PER_GROUP } from '../types/championship.js';
import type { SessionResultRow } from '../types/championship.js';
import { shuffleDriverIds } from './meetingQualification.js';

export interface MeetingStartOrderInput {
  sessionType: string;
  sessionId: number;
  numParticipants: number;
  random?: () => number;
  /** Lancers cumulés en demi — ordre de départ en finale (index = rang grille). */
  semiDiceRollsByPlayerIndex?: readonly number[];
}

/** Graine déterministe pour le tirage d’ordre de départ en qualif. */
export function meetingStartOrderSeed(sessionId: number): number {
  return sessionId * 79_019 + 17;
}

function defaultRandom(): number {
  return Math.random();
}

/**
 * Tampons d’arrivée pour demi / finale : ordre de la grille (même case → rang grille).
 * La mécanique de course est identique à la qualif (dé à chaque tour, ordre = classement).
 */
export function gridRankingTurnOrder(numParticipants: number): number[] {
  return Array.from({ length: numParticipants }, (_, i) => i);
}

/**
 * Ordre de jeu au départ d’une manche championnat :
 * - qualif : tirage au sort (grille 1 pilote / case)
 * - demi : ordre grille (classement qualif) ; tampons d’arrivée sur même case
 * - finale : moins de lancers en demi entre coéquipiers de ligne, puis d6 si égalité
 */
export function resolveMeetingInitialTurnOrder(input: MeetingStartOrderInput): number[] {
  const n = input.numParticipants;
  const indices = Array.from({ length: n }, (_, i) => i);
  const random = input.random ?? defaultRandom;

  if (input.sessionType.startsWith('qual')) {
    return shuffleDriverIds(indices, random);
  }

  if (input.sessionType.startsWith('semi')) {
    return gridRankingTurnOrder(n);
  }

  if (input.sessionType === 'final') {
    const semiDice = input.semiDiceRollsByPlayerIndex;
    if (semiDice && semiDice.length === n) {
      return resolveFinalStartTurnOrder(n, semiDice, random);
    }
    return gridRankingTurnOrder(n);
  }

  return indices;
}

/** Finale : ordre de lancer au départ, par ligne de grille (2 pilotes / ligne). */
export function resolveFinalStartTurnOrder(
  numParticipants: number,
  semiDiceRollsByPlayerIndex: readonly number[],
  random: () => number,
  carsPerRow = 2
): number[] {
  const rollD6 = (): number => Math.floor(random() * 6) + 1;
  const result: number[] = [];
  const numRows = Math.ceil(numParticipants / carsPerRow);

  for (let row = 0; row < numRows; row++) {
    const pair: number[] = [];
    for (let col = 0; col < carsPerRow; col++) {
      const idx = row * carsPerRow + col;
      if (idx < numParticipants) pair.push(idx);
    }
    if (pair.length === 1) {
      result.push(pair[0]);
      continue;
    }
    const [a, b] = pair;
    const diceA = semiDiceRollsByPlayerIndex[a] ?? 0;
    const diceB = semiDiceRollsByPlayerIndex[b] ?? 0;
    if (diceA < diceB) {
      result.push(a, b);
    } else if (diceB < diceA) {
      result.push(b, a);
    } else {
      const rollA = rollD6();
      const rollB = rollD6();
      if (rollA < rollB) result.push(a, b);
      else if (rollB < rollA) result.push(b, a);
      else result.push(a, b);
    }
  }
  return result;
}

/** Tampons d’arrivée cohérents avec l’ordre de jeu initial (tie-break sur case partagée). */
export function arrivalStampsForTurnOrder(
  numParticipants: number,
  turnOrder: readonly number[]
): number[] {
  const stampByPlayer = new Map<number, number>();
  turnOrder.forEach((playerIndex, slot) => stampByPlayer.set(playerIndex, slot + 1));
  return Array.from({ length: numParticipants }, (_, i) => stampByPlayer.get(i) ?? i + 1);
}

/** Lancers de dé en demi alignés sur l’ordre de la grille finale. */
export function semiDiceRollsByPlayerIndexForGrid(
  gridDriverIds: readonly number[],
  semiResults: readonly SessionResultRow[]
): number[] {
  const byDriver = new Map<number, number>();
  for (const r of semiResults) {
    byDriver.set(r.driverId, r.diceRolls);
  }
  return gridDriverIds.map((id) => byDriver.get(id) ?? 0);
}

/** Grille finale : 1er demi A + 1er demi B en ligne 1, 2e + 2e en ligne 2, etc. */
export function buildFinalGridDriverIds(
  semiAResults: readonly SessionResultRow[],
  semiBResults: readonly SessionResultRow[],
  maxDrivers = 6
): number[] {
  const byPosition = (rows: readonly SessionResultRow[]) =>
    [...rows].sort((a, b) => a.position - b.position);
  const a = byPosition(semiAResults);
  const b = byPosition(semiBResults);
  const out: number[] = [];
  for (let rank = 0; rank < SEMI_ADVANCE_PER_GROUP; rank++) {
    if (a[rank]) out.push(a[rank].driverId);
    if (b[rank]) out.push(b[rank].driverId);
  }
  return out.slice(0, maxDrivers);
}
